import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  getListingMasterlocks,
  validateMasterlockDevice,
} from 'api/MasterLockController';
import errorReporter from '@app/errorReporter';
import Swal from 'sweetalert2';
import { ListingLabelContext } from 'contexts';
import { ConfirmationInstructions } from '@bb/domain/dist/types/AppointmentConfig';
import { featureEnabled } from 'utils';
import { ListingMasterlocks } from 'api/MasterLockController/getListingMasterlocks';
import { Select, SelectOption } from '../Select';

interface MasterlockValidatorProps {
  shouldGetLockboxes: boolean;
  listingId: string;
  deviceId: string;
  resetAppointmentConfigAccess: () => void;
  setIsMasterLockCodeValid: (isValid: boolean) => void;
  setIsMasterLockOneDayCodeEnabled: (enabled: boolean) => void;
  setGetLockboxesFalse: () => void;
  onValidate: (deviceId: string) => void;
  instructions: ConfirmationInstructions['masterlock'];
}

const MasterlockValidator = ({
  shouldGetLockboxes,
  listingId,
  deviceId,
  resetAppointmentConfigAccess,
  setIsMasterLockCodeValid,
  setIsMasterLockOneDayCodeEnabled,
  setGetLockboxesFalse,
  onValidate,
  instructions,
}: MasterlockValidatorProps) => {
  const [locks, setLocks] = useState<ListingMasterlocks>([]);
  const [selected, setSelected] = useState<ListingMasterlocks[number] | null>(
    null,
  );
  const [isSelectedLockboxValid, setIsSelectedLockboxValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isOneDayCodeEnabled, setIsOneDayCodeEnabled] = useState(
    instructions?.isOneDayCodeEnabled ?? false,
  );
  const [isOneDayCodeAvailable, setIsOneDayCodeAvailable] = useState(false);
  const { getAvailability } = React.useContext(ListingLabelContext);

  const deselect = () => {
    setSelected(null);
    setIsSelectedLockboxValid(false);
    setIsOneDayCodeEnabled(false);
    onValidate(null!);
  };

  const select = (selectedLock: (typeof locks)[number]) => {
    if (selectedLock.listing && selectedLock.listing._id !== listingId) {
      Swal.fire({
        title: 'Lockbox Is Already Assigned',
        html: ` <div style="font-weight: 200;"><div>This lockbox is already assigned to another listing: </div>
              <br/>
              <div>${selectedLock.listing.line1} ${
                selectedLock.listing.line2 || ''
              }</div>
              <div>Listing Status: ${getAvailability(
                selectedLock.listing.availability,
              )}</div></div>
              <br/>
              <div>Would you like to remove it and re-assign it to this listing?</div>
              `,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#62cb31',
        confirmButtonText: 'Yes, re-assign',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.dismiss) {
          deselect();
        } else {
          validateDeviceId(selectedLock);
        }
      });
    } else {
      validateDeviceId(selectedLock);
    }
  };

  const validateDeviceId = async (selectedLock: (typeof locks)[number]) => {
    if (!selectedLock) return;
    try {
      setIsValidating(true);
      const {
        isValid,
        isOneDayCodeAvailable: _isOneDayCodeAvailable,
        defaultOneDayCodeEnabled,
      } = await validateMasterlockDevice(selectedLock.serialNumber, listingId);
      if (!isValid) {
        setIsSelectedLockboxValid(false);
        return;
      }
      setIsOneDayCodeAvailable(_isOneDayCodeAvailable);
      setIsOneDayCodeEnabled(
        instructions?.isOneDayCodeEnabled ?? defaultOneDayCodeEnabled,
      );
      onValidate(selectedLock.serialNumber);
      setIsSelectedLockboxValid(true);
    } catch (err: any) {
      errorReporter.error(err);
      deselect();
    } finally {
      setIsValidating(false);
    }
  };

  const getLocks = async () => {
    try {
      setIsLoading(true);
      const fetchedLocks = await getListingMasterlocks(listingId);
      if (
        fetchedLocks &&
        locks &&
        fetchedLocks.length < locks.length &&
        !fetchedLocks.find(lock => lock.serialNumber === deviceId)
      ) {
        setIsSelectedLockboxValid(false);
        resetAppointmentConfigAccess();
      }
      if (!fetchedLocks.length) {
        setIsSelectedLockboxValid(false);
      }
      setLocks(fetchedLocks);
      const currentLockbox = fetchedLocks.find(
        lock => lock.serialNumber === deviceId,
      );
      setSelected(currentLockbox);
      if (currentLockbox) {
        await validateDeviceId(currentLockbox);
      } else {
        deselect();
      }
    } catch (e) {
      errorReporter.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (listingId) {
      getLocks();
    }
  }, [listingId]);

  useEffect(() => {
    if (shouldGetLockboxes) {
      getLocks();
      setGetLockboxesFalse();
    }
  }, [shouldGetLockboxes]);

  // updating variable in parent appointment_config angular component
  useEffect(() => {
    setIsMasterLockCodeValid(isSelectedLockboxValid);
  }, [isSelectedLockboxValid]);

  useEffect(() => {
    setIsMasterLockOneDayCodeEnabled(isOneDayCodeEnabled);
  }, [isOneDayCodeEnabled]);

  const handleOnOneDayCodeEnabled = (e: any) => {
    setIsOneDayCodeEnabled(e.target.checked);
  };

  return (
    <div>
      <StyledMasterlockContainer>
        {!selected && locks.length > 0 && (
          <Select
            showSearch
            placeholder="Select"
            value={selected?.serialNumber}
            onChange={value => {
              const selectedLock = locks.find(
                ({ serialNumber }) => serialNumber === value,
              );
              setSelected({ ...selectedLock });
              select({ ...selectedLock });
            }}
          >
            {locks?.map(lock => (
              <SelectOption key={lock.serialNumber} value={lock.serialNumber}>
                {lock.serialNumber}
              </SelectOption>
            ))}
          </Select>
        )}
        {selected && (
          <div className="form-inline">
            <div className="form-group">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={selected?.serialNumber}
                  disabled
                />
                <span className="input-group-btn">
                  <button onClick={deselect} className="btn btn-danger">
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
        )}
        {!locks?.length && !isLoading && (
          <a
            href="/#/lockboxes"
            className="masterlock-validator-add-lockbox-link"
          >
            <label style={{ cursor: 'pointer' }} className="error">
              No lock boxes found. Click here to add a lockbox.
            </label>
          </a>
        )}
      </StyledMasterlockContainer>
      {isValidating && <div>Validating...</div>}
      {locks?.length > 0 && selected && !isLoading && !isValidating && (
        <div>
          {isSelectedLockboxValid && (
            <div>
              <>
                {!isOneDayCodeAvailable ? (
                  <div>No Manual Code available.</div>
                ) : (
                  <div>
                    <input
                      type="checkbox"
                      checked={isOneDayCodeEnabled}
                      onChange={handleOnOneDayCodeEnabled}
                    />
                    &nbsp; Enable Manual Code
                  </div>
                )}
              </>

              <label style={{ margin: '5px' }} className="text-success">
                &nbsp;&#10003; The Master Lock Device ID is valid.
              </label>
            </div>
          )}
          {!isSelectedLockboxValid && (
            <div>
              <label style={{ margin: '5px' }} className="error">
                The Master Lock Device ID is invalid.
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterlockValidator;

const StyledMasterlockContainer = styled.div`
  position: relative;
  > input.form-control {
    display: inline-block;
    width: 75%;
    margin: 0;
    border-radius: 4px 0 0 4px;
    &.valid {
      border-color: #62cb31 !important;
    }
    &.invalid {
      border-color: #e74c3c !important;
    }
  }
  > button {
    position: absolute;
    display: inline-block;
    width: 25%;
    height: 100%;
    margin: 0;
    right: 0;
    border-left: 0;
    border-radius: 0 4px 4px 0;
  }
`;
